import { category } from '@alison/ui';
import type { SvgIconComponent } from '@mui/icons-material';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import PermMediaOutlined from '@mui/icons-material/PermMediaOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';

export type CompetitionFilterKey =
  | 'competitor'
  | 'country'
  | 'creativeType'
  | 'concept'
  | 'feature'
  | 'tag'
  | 'format'
  | 'duration'
  | 'creativeId';

export type CompetitionFilterKind = 'panel' | 'name';

export type CompetitionFamilyId = 'breakdown' | 'conceptual';

export interface CompetitionFamily {
  label: string;
  tone: string;
}

export interface CompetitionFilterDefinition {
  key: CompetitionFilterKey;
  label: string;
  icon: SvgIconComponent;
  kind: CompetitionFilterKind;
  family: CompetitionFamilyId;
  dependsOn?: CompetitionFilterKey;
}

export const COMPETITION_FAMILIES: Record<CompetitionFamilyId, CompetitionFamily> = {
  breakdown: { label: 'Performance breakdown axes', tone: category.attract.main },
  conceptual: { label: 'Creative & conceptual attributes', tone: category.brand.main },
};

export const COMPETITION_FAMILY_ORDER: CompetitionFamilyId[] = ['breakdown', 'conceptual'];

export const COMPETITION_SCOPE_KEYS: CompetitionFilterKey[] = ['competitor', 'creativeType'];

export const COMPETITION_FILTER_CATALOG: CompetitionFilterDefinition[] = [
  {
    key: 'competitor',
    label: 'Competitor',
    icon: BusinessOutlined,
    kind: 'panel',
    family: 'breakdown',
  },
  {
    key: 'creativeType',
    label: 'Creative Type',
    icon: PermMediaOutlined,
    kind: 'panel',
    family: 'conceptual',
  },
  { key: 'country', label: 'Country', icon: ExploreOutlined, kind: 'panel', family: 'breakdown' },
  { key: 'format', label: 'Format', icon: GridViewOutlined, kind: 'panel', family: 'conceptual' },
  {
    key: 'duration',
    label: 'Duration',
    icon: ScheduleOutlined,
    kind: 'panel',
    family: 'conceptual',
  },
  {
    key: 'creativeId',
    label: 'Creative ID',
    icon: FingerprintOutlined,
    kind: 'name',
    family: 'conceptual',
  },
  {
    key: 'concept',
    label: 'Concept',
    icon: LightbulbOutlined,
    kind: 'panel',
    family: 'conceptual',
  },
  {
    key: 'feature',
    label: 'Element',
    icon: ExtensionOutlined,
    kind: 'panel',
    family: 'conceptual',
  },
  {
    key: 'tag',
    label: 'Tag',
    icon: SellOutlined,
    kind: 'panel',
    family: 'conceptual',
    dependsOn: 'feature',
  },
];

export const COMPETITION_FILTER_INDEX = Object.fromEntries(
  COMPETITION_FILTER_CATALOG.map((definition) => [definition.key, definition]),
) as Record<CompetitionFilterKey, CompetitionFilterDefinition>;

export const COMPETITION_FILTER_KEYS = COMPETITION_FILTER_CATALOG.map(
  (definition) => definition.key,
);

export const COMPETITION_ADDABLE_CATALOG = COMPETITION_FILTER_CATALOG.filter(
  (definition) => !COMPETITION_SCOPE_KEYS.includes(definition.key),
);

export const COMPETITION_NARROWING_KEYS = new Set<CompetitionFilterKey>(
  COMPETITION_FILTER_KEYS.filter((key) => key !== 'duration' && key !== 'creativeId'),
);
